import Component from '@glimmer/component';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { expandProperties } from '@ember/object/computed';
import { addObserver, removeObserver } from '@ember/object/observers';
import EventBusService from 'cio/services/event-bus';
import TemplateModel from 'cio/models/template';
import ActionComposer, { ActionComposerArgs } from 'cio/components/action-composer';

export type BaseComposerArgs = ActionComposerArgs &
  Pick<
    ActionComposer,
    | 'selectedCustomer'
    | 'selectedEvent'
    | 'selectedTrigger'
    | 'selectedObject'
    | 'preview'
    | 'isShowingEditor'
    | 'isShowingMergeData'
    | 'onRollback'
    | 'onSave'
    | 'relatedObjects'
  > & {
    onFetchPreview: ActionComposer['fetchPreview']['perform'];
    toggleView: ActionComposer['toggleView'];
  };

interface BaseComposerSignature {
  Args: BaseComposerArgs;
}

// BaseComposer is a base class that should be extended to create composer components.
export default abstract class BaseComposer<
  T extends BaseComposerSignature = BaseComposerSignature,
> extends Component<T> {
  @service eventBus!: EventBusService;

  abstract get previewURL(): string;
  abstract previewObserverKeys: string[];
  abstract getPreviewData(): Record<string, any> | null;
  abstract get isDirty(): boolean;

  @tracked currentTemplate: TemplateModel;
  watchedTemplates: Set<TemplateModel> = new Set();

  constructor(owner: unknown, args: T['Args']) {
    super(owner, args);
    this.currentTemplate = this.args.editableEntity.template as TemplateModel;
  }

  get template() {
    // Template argument will change when switching between languages.
    // Use the tracked property to trigger reactivity when the template changes.
    let template = this.args.editableEntity.template as TemplateModel;
    if (template !== this.currentTemplate) {
      this.currentTemplate = template;
      this.watchTemplate();
    }

    return this.currentTemplate;
  }

  @action
  fetchPreview() {
    return this.args.onFetchPreview(this.previewURL, this.getPreviewData.bind(this));
  }

  @action
  previewDidChange() {
    scheduleOnce('afterRender', this, this.fetchPreview);
  }

  watchTemplate() {
    if (!this.watchedTemplates.has(this.currentTemplate)) {
      this.templateObservableProperties.forEach((prop) =>
        addObserver(this.currentTemplate, prop as keyof TemplateModel, this, 'previewDidChange'),
      );
      this.watchedTemplates.add(this.currentTemplate);
    }
  }

  unwatchTemplates() {
    this.watchedTemplates.forEach((template) => {
      this.templateObservableProperties.forEach((prop) =>
        removeObserver(template, prop as keyof TemplateModel, this, 'previewDidChange'),
      );
    });
    this.watchedTemplates.clear();
  }

  willDestroy() {
    super.willDestroy();
    this.unwatchTemplates();
  }

  private get templateObservableProperties() {
    let props: string[] = [];

    this.previewObserverKeys?.forEach((key) => {
      expandProperties(key, (prop: string) => props.push(prop.replace('args.editableEntity.template.', '')));
    });

    return props;
  }
}
